/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import {
  Button,
  Container,
  Grid,
  Header,
  Icon,
  Image,
  Label,
} from 'semantic-ui-react';
import moment from 'moment-timezone';
import _ from 'lodash';
import config from '../../config';
import ctosLogo from '../assets/images/ctos/ctos.png';
import LoanAmount from './LoanAmount';
import CurrencySymbol from './CurrencySymbol';
import CreditCheckInfoModal from './CreditCheckInfoModal';
import { fetchM } from '../actions/fetchM';
import { flashErrorMessage } from '../actions/flash';

class CTOSResult extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static mapStateToProps = state => ({
    auth: state.auth,
  })
  static propTypes = {
    auth: PropTypes.shape({
      isBranchLeader: PropTypes.bool,
      isHeadquarters: PropTypes.bool,
      isSuperAdmin: PropTypes.bool,
      isDeveloperStaff: PropTypes.bool,
      isExternalAgentLeader: PropTypes.bool,
      isExternalAgent: PropTypes.bool,
      isExternalAgencyAdmin: PropTypes.bool,
    }),
    customerData: PropTypes.shape({
      customer: PropTypes.shape({
        customerCreditReport: PropTypes.shape({
          id: PropTypes.string,
          ctosScore: PropTypes.string,
          dsr: PropTypes.number,
          dsrScoreAfter: PropTypes.number,
          incomeNet: PropTypes.number,
          currentLoanAmount: PropTypes.number,
          mCtosHTMLRequestID: PropTypes.string,
        }),
      }),
    }),
    customerDataID: PropTypes.string,
    data: PropTypes.shape({
      id: PropTypes.string,
    }),
  }
  constructor(props) {
    super(props);
    this.state = {
      companyApp: [],
      ctosColor: '',
      ctosColorShadow: '',
      ctosStatus: '',
      loading: false,
      openCTOSModal: false,
      openDSRModal: false,
    };
  }
  componentDidMount() {
    this.getCtosInfo();
    if (this.props.auth &&
      (this.props.auth.isBranchLeader ||
        this.props.auth.isDeveloperStaff ||
        this.props.auth.isExternalAgencyAdmin ||
        this.props.auth.isExternalAgent ||
        this.props.auth.isExternalAgentLeader)
    ) {
      this.fetchCompanyApp();
    }
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, res);
    });
  }
  getCtosInfo() {
    const { customerData } = this.props;
    const ctosScore = _.toNumber(customerData.customer.customerCreditReport.ctosScore);

    switch (ctosScore !== 0) {
      case ctosScore > 0 && ctosScore < 300:
        this.setState({ ctosColor: 'rgba(206, 0, 0, 1)', ctosColorShadow: 'rgba(206, 0, 0, 0.53)', ctosStatus: 'No Score' });
        break;
      case ctosScore > 299 && ctosScore < 529:
        this.setState({ ctosColor: 'rgba(206, 0, 0, 1)', ctosColorShadow: 'rgba(206, 0, 0, 0.53)', ctosStatus: 'Very Weak' });
        break;
      case ctosScore > 528 && ctosScore < 651:
        this.setState({ ctosColor: 'rgba(247, 148, 30, 1)', ctosColorShadow: 'rgba(247, 148, 30, 0.53)', ctosStatus: 'Weak' });
        break;
      case ctosScore > 650 && ctosScore < 697:
        this.setState({ ctosColor: 'rgba(242, 227, 0, 1)', ctosColorShadow: 'rgba(242, 227, 0, 0.53)', ctosStatus: 'Below Average' });
        break;
      case ctosScore > 696 && ctosScore < 718:
        this.setState({ ctosColor: 'rgba(216, 218, 23, 1)', ctosColorShadow: 'rgba(216, 218, 23, 0.53)', ctosStatus: 'Above Average' });
        break;
      case ctosScore > 717 && ctosScore < 744:
        this.setState({ ctosColor: 'rgba(166, 206, 57, 1)', ctosColorShadow: 'rgba(166, 206, 57, 0.53)', ctosStatus: 'Good' });
        break;
      case (ctosScore > 743):
        this.setState({ ctosColor: 'rgba(0, 152, 74, 1)', ctosColorShadow: 'rgba(0, 152, 74, 0.53)', ctosStatus: 'Excellent' });
        break;
      default:
        break;
    }
  }
  getLoanAmount() {
    const { customerData } = this.props;
    const dsrBaseRate = 4.45 / 100;
    const dsrBaseRatePerMonth = dsrBaseRate / 12;

    // get birth year of purchaser
    const ICNumber = customerData.customer.identity;
    const firstICDigit = ICNumber.substring(0, 1);
    const firstTwoICDigits = ICNumber.substring(0, 2);
    let bornYear = 0;
    if (firstICDigit === '0') {
      const str1 = '20';
      bornYear = _.toNumber(str1.concat(firstTwoICDigits));
    } else {
      const str1 = '19';
      bornYear = _.toNumber(str1.concat(firstTwoICDigits));
    }

    // get current year
    const d = moment().toDate();
    const currentYear = _.toNumber(d.getFullYear());

    // get age of purchaser
    const purchaserAge = _.toNumber(currentYear) - _.toNumber(bornYear);

    // get loan tenure based on age
    let tenure = 35;
    if ((_.toNumber(purchaserAge) + _.toNumber(tenure)) > 70) {
      tenure = 70 - purchaserAge;
      if (tenure <= 0) {
        tenure = 0;
      }
    }

    let prevMonthlyCommitment = 0;

    if (customerData.customer.customerCreditReport.dsr !== 0) {
      // get user commitment before loan
      const dsrBefore = _.toNumber(customerData.customer.customerCreditReport.dsr) / 100;
      prevMonthlyCommitment = _.toNumber(customerData.customer.customerCreditReport.incomeNet) * _.toNumber(dsrBefore);
    }

    // get user max affordable commitment
    const maxDsr = 0.6;
    const maxMonthlyCommitment = _.toNumber(customerData.customer.customerCreditReport.incomeNet) * maxDsr;
    const affordableMonthlyCommitment = _.toNumber(maxMonthlyCommitment) - _.toNumber(prevMonthlyCommitment);

    // calculate max loan amount
    const pv = _.toNumber(affordableMonthlyCommitment) / _.toNumber(dsrBaseRatePerMonth);
    const n = -(_.toNumber(tenure) * 12);
    const rate = 1 + _.toNumber(dsrBaseRatePerMonth);
    const pRate = _.toNumber(rate) ** _.toNumber(n);
    const subPRate = 1 - _.toNumber(pRate);
    return _.toNumber(pv) * _.toNumber(subPRate);
  }
  ctosScore = [
    { color: 'rgba(206, 0, 0, 1)', name: 'Very Weak', value: '300-528' },
    { color: 'rgba(247, 148, 30, 1)', name: 'Weak', value: '529-650' },
    { color: 'rgba(242, 227, 0, 1)', name: 'Below Average', value: '651-696' },
    { color: 'rgba(216, 218, 23, 1)', name: 'Above Average', value: '697-717' },
    { color: 'rgba(166, 206, 57, 1)', name: 'Good', value: '718-743' },
    { color: 'rgba(0, 152, 74, 1)', name: 'Excellent', value: '744-850' },
  ];
  async fetchCompanyApp() {
    const { store } = this.context;
    await this.setLoading(true);
    let res;
    try {
      res = await store.dispatch(fetchM('/company/company_app/', {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const companyApp = await res.json();
      if (companyApp !== null) {
        this.setState({ companyApp });
      }
    }
    await this.setLoading(false);
  }
  handleOpenHTML = (e, customerData) => {
    e.preventDefault();
    const url = `${config.get('bookApiURL')}/customers/${this.props.customerDataID}/credit_reports/${customerData.customer.customerCreditReport.id}/response/${customerData.customer.customerCreditReport.mCtosHTMLRequestID}/`;
    window.open(url, `credit-report-${customerData.customer.customerCreditReport.mCtosHTMLRequestID}`, '');
  }
  showCTOSInfo = () => {
    this.setState({ openCTOSModal: true, openDSRModal: false });
  }
  showDSRInfo = () => {
    this.setState({ openDSRModal: true, openCTOSModal: false });
  }
  render() {
    const { store } = this.context;
    const { auth, customerData, data } = this.props;
    const { companyApp, ctosColor, ctosColorShadow, ctosStatus } = this.state;
    return (
      <Grid centered stackable>
        <Grid.Row>
          <Grid.Column computer={7} tablet={8} mobile={8}>
            <Container
              style={{
                borderRadius: '0.25rem',
                boxShadow: customerData.customer.customerCreditReport.mCtosXMLStatusID !== 'dsr_generated' ? '1px 1px 1px 2px rgba(0, 0, 0, 0.16)' : '0px 1px 0px 1px rgba(0, 0, 0, 0.16)',
                height: customerData.customer.customerCreditReport.mCtosXMLStatusID !== 'dsr_generated' ? '20rem' : '36rem',
              }}
            >
              {
                customerData.customer.customerCreditReport.mCtosXMLStatusID !== 'dsr_generated' ? (
                  <div>
                    <Header as="h2" style={{ fontSize: '12px', color: 'black', paddingTop: '2rem' }}>
                      <div className="ctosDsrHeader">
                        CTOS <Button onClick={this.showCTOSInfo}><p style={{ padding: '0 0.5rem', display: 'inline', border: '1px solid', borderRadius: '50%' }}>i</p></Button>
                      </div>
                    </Header>
                    <Icon name="clock" style={{ color: 'orange', marginTop: '4rem', fontSize: '5rem', height: '4rem !important' }} />
                    <Header as="h4" style={{ marginTop: '0' }}>
                      <Header.Content style={{ color: 'orange' }}>
                        Pending Request
                      </Header.Content>
                      <Header.Subheader style={{ color: 'orange' }}>
                        Please check again later
                      </Header.Subheader>
                    </Header>
                  </div>
                ) : (
                  <div>
                    <Grid.Row>
                      <Grid.Column>
                        <Container
                          className="ctosScoreContainer"
                          style={{
                            background: ctosColor,
                            boxShadow: `0px 3px 0px 0px ${ctosColorShadow}`,
                          }}
                        >
                          <Header as="h2" style={{ fontSize: '12px', color: 'white', paddingTop: '2rem', marginBottom: '0' }}>
                            <div className="ctosDsrHeader">
                              CTOS <Button onClick={this.showCTOSInfo}><p style={{ padding: '0 0.5rem', display: 'inline', border: '1px solid', borderRadius: '50%', color: 'white' }}>i</p></Button>
                            </div>
                          </Header>
                          <Header
                            as="h1"
                            content={customerData.customer.customerCreditReport.ctosScore}
                            style={{ color: 'white', margin: 0 }}
                          />
                          <Label
                            content={ctosStatus}
                            style={{ background: 'transparent', color: 'white' }}
                          />
                        </Container>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row style={{ padding: '1rem' }}>
                      <Grid.Column>
                        <Header
                          as="h4"
                          content="CTOS Scoring"
                          textAlign="left"
                          style={{ margin: '1rem' }}
                        />
                        {
                          this.ctosScore.map((ctosScore, key) => (
                            <Label as="div" key={key} style={{ width: '100%', background: 'transparent' }}>
                              <Icon name="circle" style={{ color: ctosScore.color, float: 'left' }} />
                              <p style={{ display: 'inline', float: 'left', margin: 0 }}>{ctosScore.name}</p>
                              <p style={{ display: 'inline', float: 'right', margin: 0 }}>{ctosScore.value}</p>
                            </Label>
                          ))
                        }
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      {
                        auth &&
                        (
                          auth.isHeadquarters ||
                          auth.isSuperAdmin ||
                          (companyApp && companyApp.allowBranchLeaderDownload && auth.isBranchLeader) ||
                          (companyApp && companyApp.allowStaffDownload && auth.isDeveloperStaff) ||
                          (companyApp && companyApp.allowAgentDownload && (auth.isExternalAgentLeader || auth.isExternalAgent || auth.isExternalAgencyAdmin))
                        ) ? (
                          <div className="ctosButton">
                            <Button
                              basic
                              content="Full Report"
                              onClick={e => this.handleOpenHTML(e, customerData)}
                            />
                          </div>
                        ) : null
                      }
                    </Grid.Row>
                  </div>
                )
              }
              <Grid>
                <Grid.Row>
                  <Grid.Column>
                    <CreditCheckInfoModal
                      open={this.state.openCTOSModal}
                      header="CTOS"
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Container>
          </Grid.Column>
          <Grid.Column computer={7} tablet={8} mobile={8}>
            <Container
              style={{
                borderRadius: '0.25rem',
                boxShadow: customerData.customer.customerCreditReport.mCtosXMLStatusID !== 'dsr_generated' ? '1px 1px 1px 2px rgba(0, 0, 0, 0.16)' : '0px 1px 0px 1px rgba(0, 0, 0, 0.16)',
                height: customerData.customer.customerCreditReport.mCtosXMLStatusID !== 'dsr_generated' ? '20rem' : '36rem',
              }}
            >
              {
                customerData.customer.customerCreditReport.mCtosXMLStatusID !== 'dsr_generated' ? (
                  <div>
                    <Header as="h2" style={{ fontSize: '12px', color: 'black', paddingTop: '2rem' }}>
                      <div className="ctosDsrHeader">
                        DSR <Button onClick={this.showDSRInfo}><p style={{ padding: '0 0.5rem', display: 'inline', border: '1px solid', borderRadius: '50%' }}>i</p></Button>
                      </div>
                    </Header>
                    <Icon name="clock" style={{ color: 'orange', marginTop: '4rem', fontSize: '5rem', height: '4rem !important' }} />
                    <Header as="h4" style={{ marginTop: '0' }}>
                      <Header.Content style={{ color: 'orange' }}>
                        Pending Request
                      </Header.Content>
                      <Header.Subheader style={{ color: 'orange' }}>
                        Please check again later
                      </Header.Subheader>
                    </Header>
                  </div>
                ) : (
                  <div>
                    <Grid.Row>
                      <Grid.Column>
                        <Container
                          style={{
                            padding: '2rem',
                            borderTopLeftRadius: '0.25rem',
                            borderTopRightRadius: '0.25rem',
                            background: 'rgba(231, 236, 241, 1)',
                            height: '10rem',
                          }}
                        >
                          <div className={customerData.customer.customerCreditReport.dsrScoreAfter > 0 ? 'ctosDSRContainerLeft' : null}>
                            <Header as="h2" style={{ fontSize: '12px', color: 'black', paddingTop: '1rem', marginBottom: '0' }}>
                              <div className="ctosDsrHeader">
                                DSR <Button onClick={this.showDSRInfo}><p style={{ padding: '0 0.5rem', display: 'inline', border: '1px solid', borderRadius: '50%' }}>i</p></Button>
                              </div>
                            </Header>
                            <Header
                              as="h1"
                              content={`${customerData.customer.customerCreditReport.dsr}%`}
                              style={{ color: 'black', margin: 0 }}
                            />
                          </div>
                          {
                            customerData.customer.customerCreditReport.dsrScoreAfter > 0 ? (
                              <div className="ctosDSRContainerRight">
                                <Header as="h2" style={{ fontSize: '12px', color: 'black', paddingTop: '1rem', marginBottom: '0' }}>
                                  DSR After
                                </Header>
                                <Header
                                  as="h1"
                                  content={`${customerData.customer.customerCreditReport.dsrScoreAfter}%`}
                                  style={{ color: 'black', margin: 0 }}
                                />
                              </div>
                            ) : null
                          }
                        </Container>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row style={{ padding: '1rem', marginTop: '0.5rem' }}>
                      <Grid.Column>
                        <Label as="div" style={{ width: '100%', background: 'transparent' }}>
                          <Header as="h5" style={{ float: 'left', margin: 0 }} content="Net Income" />
                          <p style={{ display: 'inline', float: 'right', margin: 0 }}>
                            <LoanAmount
                              format="0,000,000.00"
                              amount={customerData.customer.customerCreditReport.incomeNet}
                              prefix={<CurrencySymbol />}
                              precise={false}
                            />
                          </p>
                        </Label>
                        <Label as="div" style={{ width: '100%', background: 'transparent' }}>
                          <Header as="h5" style={{ float: 'left', margin: 0 }} content="Housing Loan Amount" />
                          <p style={{ display: 'inline', float: 'right', margin: 0 }}>
                            <LoanAmount
                              format="0,000,000.00"
                              amount={customerData.customer.customerCreditReport.currentLoanAmount}
                              prefix={<CurrencySymbol />}
                              precise={false}
                            />
                          </p>
                        </Label>
                        <Label as="div" style={{ width: '100%', background: 'transparent' }}>
                          <Header as="h5" style={{ float: 'left', margin: 0 }} textAlign="left">
                          Maximum Loan Amount <br /> (based on 60% DSR)
                          </Header>
                          <p style={{ display: 'inline', float: 'right', margin: 0 }}>
                            <LoanAmount
                              format="0,000,000.00"
                              amount={this.getLoanAmount()}
                              prefix={<CurrencySymbol />}
                              precise={false}
                            />
                          </p>
                        </Label>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row style={{ marginTop: '9rem' }}>
                      <div className="ctosButton">
                        <Button
                          basic
                          content="Edit Income"
                          onClick={() => {
                            store.dispatch(push(`/bookings/${data.id}/customers/${customerData.customerID}/income/${customerData.customer.incomeID}/`));
                          }}
                        />
                      </div>
                    </Grid.Row>
                  </div>
                )
              }
              <Grid>
                <Grid.Row>
                  <Grid.Column>
                    <CreditCheckInfoModal
                      open={this.state.openDSRModal}
                      header="DSR"
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Container>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row style={{ marginTop: '1rem' }}>
          <Grid.Column>
            <Label className="ctosFooterLabel" as="div" style={{ background: 'transparent', fontSize: '9px', color: 'rgba(162, 174, 183, 1)' }}>
              Powered by <Image src={ctosLogo} size="mini" />. Registrars of CRAs 100% secure
            </Label>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default connect(CTOSResult.mapStateToProps)(CTOSResult);



// WEBPACK FOOTER //
// ./src/components/CTOSResult.jsx